import { startJob } from "../../lib/api/contractor";
import { listAppointments, startAppointment } from "../../lib/api/conversation";
import type { Job } from "../../lib/types/models";

/** Appointment statuses that mean "there is nothing left to start". */
const DEAD_APPOINTMENT = new Set(["cancelled", "completed", "rescheduled", "expired"]);

/**
 * Starting work has two doors. When the job has a live appointment, the
 * appointment endpoint is the one to use: it flips the job to InProgress AND
 * marks the appointment started in the same transaction, whereas
 * `POST /jobs/:id/start` would move the job and leave the appointment
 * stranded in `Scheduled` forever.
 *
 * Shared so that the same job started from Active work and from its own detail
 * screen leaves the appointment in the same state either way.
 *
 * The appointment door answers with the appointment rather than the job, so it
 * returns null and the caller re-reads; `/jobs/:id/start` hands the moved job
 * straight back.
 */
export async function startWorkOnJob(job: Job): Promise<Job | null> {
  if (job.conversationId) {
    const appointments = await listAppointments(job.conversationId).catch(() => []);
    const live = appointments
      .filter((a) => !DEAD_APPOINTMENT.has(a.status?.toLowerCase() ?? ""))
      // Wall-clock "YYYY-MM-DD" strings sort correctly as strings, which is
      // the one comparison it is safe to do without parsing them.
      .sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate))[0];
    if (live) {
      await startAppointment(job.conversationId, live.appointmentId);
      return null;
    }
  }
  return startJob(job.jobId);
}
